export function fileKey(f: File) {
    return `${f.name}__${f.size}__${f.lastModified}`;
}

export function formatBytes(bytes: number) {
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let v = bytes;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i++;
    }
    return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function openFileInNewTab(file: File) {
    const url = URL.createObjectURL(file);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export const BEDROCK_LIMITS = {
    pdf: {
        maxFiles: 5,
        maxSizeBytes: Math.round(4 * 1024 * 1024),
        mime: "application/pdf",
    },
    images: {
        maxFiles: 20,
        maxSizeBytes: Math.round(3.5 * 1024 * 1024),
    },
} as const;

export type BedrockPrecheckReason =
    | "EMPTY"
    | "MIXED_MIMETYPE"
    | "UNSUPPORTED_MIMETYPE"
    | "TOO_MANY_FILES"
    | "FILE_TOO_LARGE";

export type BedrockPrecheckResult =
    | {
    ok: true;
    kind: "pdf" | "image";
    mime: string;
    maxFiles: number;
    maxSizeBytes: number;
}
    | {
    ok: false;
    reason: BedrockPrecheckReason;
    details?: {
        mimeSet?: string[];
        maxFiles?: number;
        maxSizeBytes?: number;
        fileName?: string;
        fileSize?: number;
    };
};

function isImageMime(mime: string) {
    return mime.startsWith("image/");
}

export function bedrockScreeningPrecheck(files: File[]): BedrockPrecheckResult {
    if (!files || files.length === 0) {
        return { ok: false, reason: "EMPTY" };
    }

    const mimeSet = Array.from(new Set(files.map((f) => (f.type || "").trim().toLowerCase())));
    if (mimeSet.length !== 1) {
        return { ok: false, reason: "MIXED_MIMETYPE", details: { mimeSet } };
    }

    const mime = mimeSet[0];

    let kind: "pdf" | "image";
    let maxFiles: number;
    let maxSizeBytes: number;

    if (mime === BEDROCK_LIMITS.pdf.mime) {
        kind = "pdf";
        maxFiles = BEDROCK_LIMITS.pdf.maxFiles;
        maxSizeBytes = BEDROCK_LIMITS.pdf.maxSizeBytes;
    } else if (isImageMime(mime)) {
        kind = "image";
        maxFiles = BEDROCK_LIMITS.images.maxFiles;
        maxSizeBytes = BEDROCK_LIMITS.images.maxSizeBytes;
    } else {
        return { ok: false, reason: "UNSUPPORTED_MIMETYPE", details: { mimeSet } };
    }

    if (files.length > maxFiles) {
        return {
            ok: false,
            reason: "TOO_MANY_FILES",
            details: { maxFiles },
        };
    }

    const tooLarge = files.find((f) => f.size > maxSizeBytes);
    if (tooLarge) {
        return {
            ok: false,
            reason: "FILE_TOO_LARGE",
            details: {
                maxSizeBytes,
                fileName: tooLarge.name,
                fileSize: tooLarge.size,
            },
        };
    }

    return { ok: true, kind, mime, maxFiles, maxSizeBytes };
}